import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import Select from "react-select";
import { Button } from "react-bootstrap";
import {
  getAllStates,
  getRegionsByStateId,
  getBranchesByRegionId,
  searchSpokesWithFilter,
} from "../../../Utils/Services/spokeMaster";

const SpokeFilter = forwardRef(({ onFilter, onClear, loading }, ref) => {
  // Filter section data (dropdowns)
  const [filterData, setFilterData] = useState({
    stateId: null,
    regionId: null,
    branchId: null,
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Search section data (text inputs)
  const [searchData, setSearchData] = useState({
    pincode: "",
    spokeName: "",
  });

  const [dropdownOptions, setDropdownOptions] = useState({
    states: [],
    regions: [],
    branches: [],
  });

  const [selectedOptions, setSelectedOptions] = useState({
    state: null,
    region: null,
    branch: null,
  });

  useEffect(() => {
    loadStates();
  }, []);

  const loadStates = async () => {
    try {
      console.log("Loading states...");
      const response = await getAllStates();
      console.log("States API Response:", response); // Debug log
      console.log("Response type:", typeof response);
      console.log("Response keys:", Object.keys(response || {}));
      
      // The HTTP interceptor wraps the response in a data property
      const actualResponse = response.data;
      console.log("Actual API Response:", actualResponse);
      
      if (actualResponse && actualResponse.result && actualResponse.status === "success") {
        console.log("Processing states from result:", actualResponse.result);
        const stateOptions = actualResponse.result.map((state) => ({
          value: state.id,
          label: state.stateName,
        }));
        console.log("State Options created:", stateOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          states: stateOptions,
        }));
        console.log("States dropdown updated");
      } else if (actualResponse && actualResponse.data) {
        console.log("Processing states from data (fallback):", actualResponse.data);
        const stateOptions = actualResponse.data.map((state) => ({
          value: state.id,
          label: state.stateName || state.name,
        }));
        console.log("State Options created (fallback):", stateOptions);
        setDropdownOptions((prev) => ({
          ...prev,
          states: stateOptions,
        }));
      } else {
        console.error("States API Error - Unexpected response structure:", actualResponse);
      }
    } catch (error) {
      console.error("Error loading states:", error);
      console.error("Error details:", error.message, error.stack);
    }
  };

  const handleStateChange = async (selectedOption) => {
    setSelectedOptions({
      state: selectedOption,
      region: null,
      branch: null,
    });

    setFilterData({
      stateId: selectedOption?.value || null,
      regionId: null,
      branchId: null,
    });

    setDropdownOptions((prev) => ({
      ...prev,
      regions: [],
      branches: [],
    }));

    if (selectedOption?.value) {
      loadRegions(selectedOption.value);
    }
  };

  const loadRegions = async (stateId) => {
    try {
      const response = await getRegionsByStateId(stateId);
      console.log("Regions API Response:", response); // Debug log
      const actualResponse = response.data;
      
      if (actualResponse && actualResponse.result && actualResponse.status === "success") {
        const regionOptions = actualResponse.result.map((region) => ({
          value: region.id,
          label: region.regionName || region.name || region.title,
        }));
        console.log("Region Options:", regionOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          regions: regionOptions,
        }));
      } else if (actualResponse && actualResponse.data && !actualResponse.error) {
        // Fallback for different response structure
        const regionOptions = actualResponse.data.map((region) => ({
          value: region.id,
          label: region.regionName || region.name || region.title,
        }));
        console.log("Region Options (fallback):", regionOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          regions: regionOptions,
        }));
      } else if (Array.isArray(actualResponse)) {
        // Handle direct array response (like the regions API)
        const regionOptions = actualResponse.map((region) => ({
          value: region.id,
          label: region.regionName || region.name || region.title,
        }));
        console.log("Region Options (array response):", regionOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          regions: regionOptions,
        }));
      } else {
        console.error("Regions API Error - Unexpected response structure:", actualResponse);
      }
    } catch (error) {
      console.error("Error loading regions:", error);
    }
  };

  const handleRegionChange = async (selectedOption) => {
    setSelectedOptions((prev) => ({
      ...prev,
      region: selectedOption,
      branch: null,
    }));

    setFilterData((prev) => ({
      ...prev,
      regionId: selectedOption?.value || null,
      branchId: null,
    }));

    setDropdownOptions((prev) => ({
      ...prev,
      branches: [],
    }));

    if (selectedOption?.value) {
      loadBranches(selectedOption.value);
    }
  };

  const loadBranches = async (regionId) => {
    try {
      const response = await getBranchesByRegionId(regionId);
      console.log("Branches API Response:", response); // Debug log
      const actualResponse = response.data;
      
      if (actualResponse && actualResponse.result && actualResponse.status === "success") {
        const branchOptions = actualResponse.result.map((branch) => ({
          value: branch.id,
          label: branch.branchName || branch.name || branch.title,
        }));
        console.log("Branch Options:", branchOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          branches: branchOptions,
        }));
      } else if (actualResponse && actualResponse.data && !actualResponse.error) {
        // Fallback for different response structure
        const branchOptions = actualResponse.data.map((branch) => ({
          value: branch.id,
          label: branch.branchName || branch.name || branch.title,
        }));
        console.log("Branch Options (fallback):", branchOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          branches: branchOptions,
        }));
      } else if (Array.isArray(actualResponse)) {
        // Handle direct array response
        const branchOptions = actualResponse.map((branch) => ({
          value: branch.id,
          label: branch.branchName || branch.name || branch.title,
        }));
        console.log("Branch Options (array response):", branchOptions); // Debug log
        setDropdownOptions((prev) => ({
          ...prev,
          branches: branchOptions,
        }));
      } else {
        console.error("Branches API Error - Unexpected response structure:", actualResponse);
      }
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const handleBranchChange = (selectedOption) => {
    setSelectedOptions((prev) => ({
      ...prev,
      branch: selectedOption,
    }));

    setFilterData((prev) => ({
      ...prev,
      branchId: selectedOption?.value || null,
    }));
  };

  // Handle pincode input change (search section)
  const handlePincodeChange = (e) => {
    const value = e.target.value;
    setSearchData((prev) => ({
      ...prev,
      pincode: value,
    }));
  };

  // Handle spoke name input change (search section)
  const handleSpokeNameChange = (e) => {
    const value = e.target.value;
    setSearchData((prev) => ({
      ...prev,
      spokeName: value,
    }));
  };



  // Handle combined filter/search button click
  const handleFilterSearch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isProcessing || loading) {
      console.log("SpokeFilter: Already processing, skipping");
      return;
    }
    
    console.log("SpokeFilter handleFilterSearch called");
    console.log("SpokeFilter handleFilterSearch stack trace:", new Error().stack);
    
    setIsProcessing(true);
    
    // Combine filter data with search data for complete payload
    const combinedData = {
      stateId: filterData.stateId,
      regionId: filterData.regionId,
      branchId: filterData.branchId,
      pincode: searchData.pincode || "",
      spokeName: searchData.spokeName || "",
    };
    onFilter(combinedData);
    
    // Reset processing state after a short delay
    setTimeout(() => {
      setIsProcessing(false);
    }, 1000);
  };

  const handleClear = () => {
    setFilterData({
      stateId: null,
      regionId: null,
      branchId: null,
    });

    setSearchData({
      pincode: "",
      spokeName: "",
    });

    setSelectedOptions({
      state: null,
      region: null,
      branch: null,
    });

    setDropdownOptions({
      states: dropdownOptions.states,
      regions: [],
      branches: [],
    });

    onClear();
  };

  // Expose clear method to parent component
  useImperativeHandle(ref, () => ({
    clear: handleClear
  }));

  return (
    <div className="filter-container p-2 mb-3" style={{ backgroundColor: "transparent", borderRadius: "6px" }}>
      <div className="spoke-filter-layout">
        {/* Combined Filter and Search Section */}
        <div className="filter-row filter-row-first">
          <div className="filter-section" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '15px', 
            marginRight: '20px',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#495057', whiteSpace: 'nowrap' }}>Filter & Search:</span>
            
            {/* State Field */}
            <div className="filter-field" style={{ minWidth: '150px' }}>
              <Select
                value={selectedOptions.state}
                onChange={handleStateChange}
                options={dropdownOptions.states}
                placeholder="State"
                isClearable
                isSearchable
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '32px',
                    fontSize: '13px'
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    padding: '2px 6px'
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: '0px'
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: '32px'
                  })
                }}
              />
            </div>

            {/* Region Field */}
            <div className="filter-field" style={{ minWidth: '150px' }}>
              <Select
                value={selectedOptions.region}
                onChange={handleRegionChange}
                options={dropdownOptions.regions}
                placeholder="Region"
                isClearable
                isSearchable
                isDisabled={!selectedOptions.state}
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '32px',
                    fontSize: '13px'
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    padding: '2px 6px'
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: '0px'
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: '32px'
                  })
                }}
              />
            </div>

            {/* Branch Field */}
            <div className="filter-field" style={{ minWidth: '150px' }}>
              <Select
                value={selectedOptions.branch}
                onChange={handleBranchChange}
                options={dropdownOptions.branches}
                placeholder="Branch"
                isClearable
                isSearchable
                isDisabled={!selectedOptions.region}
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '32px',
                    fontSize: '13px'
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    padding: '2px 6px'
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: '0px'
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: '32px'
                  })
                }}
              />
            </div>

            {/* Pincode Field */}
            <div className="filter-field" style={{ minWidth: '120px' }}>
              <input
                type="text"
                value={searchData.pincode}
                onChange={handlePincodeChange}
                placeholder="Pincode"
                className="form-control"
                style={{
                  minHeight: '32px',
                  fontSize: '13px',
                  border: searchData.pincode ? '2px solid #28a745' : '1px solid #ced4da',
                  boxShadow: searchData.pincode ? '0 0 0 0.2rem rgba(40, 167, 69, 0.25)' : 'none'
                }}
              />
            </div>

            {/* Spoke Name Field */}
            <div className="filter-field" style={{ minWidth: '120px' }}>
              <input
                type="text"
                value={searchData.spokeName}
                onChange={handleSpokeNameChange}
                placeholder="Spoke Name"
                className="form-control"
                style={{
                  minHeight: '32px',
                  fontSize: '13px',
                  border: searchData.spokeName ? '2px solid #28a745' : '1px solid #ced4da',
                  boxShadow: searchData.spokeName ? '0 0 0 0.2rem rgba(40, 167, 69, 0.25)' : 'none'
                }}
              />
            </div>

            {/* Combined Filter/Search Button */}
            <Button
              variant="primary"
              onClick={handleFilterSearch}
              disabled={loading || isProcessing}
              size="sm"
              className="px-2 py-1"
              style={{ fontSize: '12px', minWidth: '80px' }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Search
                </>
              ) : (
                'Search'
              )}
            </Button>

            {/* Clear Button */}
            <Button
              variant="outline-secondary"
              onClick={handleClear}
              disabled={loading}
              size="sm"
              className="px-2 py-1"
              style={{ fontSize: '12px', minWidth: '60px' }}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default SpokeFilter;
